// Дебиторка: смарт-процесс 134 «Платежи».
//
// Здесь живут ТОЛЬКО правила, а не разметка: какие поля читаем, что считаем оплаченным,
// как считаем остаток и просрочку. Причина — у порталов 55 и exec эти правила разные
// (см. `ДЕБИТОРКА_карта_полей.md` §5 и §7), и повторить расхождение в третьем месте нельзя.
// Одно место = один ответ на «почему цифры такие».
//
// Названия полей в интерфейс отсюда НЕ попадают: их даёт `/fields` (useFields('item:134')).
// Здесь только машинные коды — без них нечего запрашивать у ядра.

import type { Payment } from '../api/types'
import { daysPast, isoDay, todayIso } from './format'
import type { Money } from './money'
import { parseMoney, toNumber } from './money'
import type { RefsApi, StageKind } from './refs'
// Чистка «пустой привязки» одна на всю CRM: Bitrix пишет «ничего не привязано» нулём,
// и второй такой функции здесь быть не должно — иначе в одном разделе «#0» почистится,
// а в другом нет. См. lib/smart.ts.
import { refId } from './smart'

/** Тип смарт-процесса и область справочника полей. */
export const PAY_TYPE_ID = 134
export const PAY_ENTITY = 'item:134'

/**
 * Коды полей 134. Проверены на живом ядре (сумма, дата, номер счёта, компания, сделка),
 * остальные взяты из карты полей — если какого-то в Bitrix не окажется, значение просто
 * не придёт, и экран покажет «—», а не соврёт.
 */
export const PAY = {
  /** «Сумма платежа», money-строка «515000|RUB». Стандартное opportunity часто 0. */
  value: 'ufCrm22Value',
  /** «Сумма для долгов» (число). Кто её пишет — человек или БП — пока неизвестно. */
  debtSum: 'ufCrm22_1744288645',
  /** «Сумма поступления оплаты» (число) — сколько уже пришло. */
  received: 'ufCrm22_1713167217',
  /** «Оплачен» — дата фактической оплаты. На 55 это единственный признак оплаты. */
  paidDate: 'ufCrm22_1712921549',
  /** «Оплачен/Не оплачен» — флаг. */
  paidFlag: 'ufCrm22Paid',
  /** «Примерная дата оплаты» — срок. Именно по нему считается просрочка. */
  dueDate: 'ufCrm22Date',
  /** «Дата оплаты по договору» — юридический срок, не тот, по которому работают. */
  contractDate: 'ufCrm22_1749459555',
  /** «Отгружен» — строка ДД.ММ.ГГГГ (формат в Bitrix не гарантирован). */
  shipped: 'ufCrm22_1727942053',
  /** «Номер счета». Может быть множественным. */
  invoice: 'ufCrm22_1713163359',
  /** «Компания» — поле-привязка; в порталах не читалось ни разу, проверить на живых данных. */
  companyField: 'ufCrm22_111111',
  /** «Сущность сделка». Родитель дублируется в системном parentId2. */
  deal: 'ufCrm22Deal',
  /** «Принадлежность к подразделению» — бизнес-юнит. */
  dept: 'ufCrm22_1727937014',
  /** «Ответственный за дебиторку» — первый в цепочке ответственных. */
  respDebt: 'ufCrm22_1775741470',
  respManager: 'ufCrm22_1729871914',
  respCoord: 'ufCrm22_1730124594',
  coordComment: 'ufCrm22_1732112208710',
  opComment: 'ufCrm22_1759744991',
  /** Номер и сумма УПД — их пишет бизнес-процесс, руками не править. */
  updNum: 'ufCrm22_1754318205',
  updSum: 'ufCrm22_1754318227',
  ourCompany: 'ufCrm22_1726207743',
  num: 'ufCrm22Num',
} as const

/**
 * Воронка «Оплачено». id зашит намеренно и ровно в одном месте: из данных его не вывести —
 * в `/refs` у воронок есть только названия, а опираться на название («Оплачено») —
 * то же самое зашивание, только хрупче. Пересоздадут воронку — правка здесь.
 */
export const PAID_CATEGORY_ID = '99'

/**
 * Что просим у ядра в списке. Явный select, а не `uf_*`: страница — это 50 сырых записей
 * смарт-процесса, в каждой под сотню полей, и таскать их целиком незачем.
 */
export const PAY_LIST_SELECT = [
  'id', 'title', 'stageId', 'categoryId', 'assignedById', 'companyId', 'parentId2',
  'opportunity', 'currencyId', 'updatedTime',
  PAY.value, PAY.debtSum, PAY.received, PAY.paidDate, PAY.paidFlag, PAY.dueDate,
  PAY.invoice, PAY.deal, PAY.dept, PAY.respDebt, PAY.shipped,
]

/** Поля, которые экран показывает и (в следующей вехе) собирался бы править. */
export const PAY_EDITABLE_LATER = [PAY.dueDate, PAY.coordComment, PAY.opComment]
/** Поля, которые считает бизнес-процесс: их нельзя править никогда. */
export const PAY_BP_WRITTEN = [PAY.updNum, PAY.updSum]

// ─────────────────────────── пробные записи ───────────────────────────
//
// В боевом Bitrix живут пробные платежи: их заводят при обкатке бизнес-процессов, обучении
// и отладке рассылок. Чистить их нельзя — владелец сказал прямо, и правило это общее
// (`CLAUDE.md`, «В Bitrix ничего не писать и ничего не чистить без спроса»). Значит задача
// зеркала — не убрать, а НАЗВАТЬ: человек должен видеть, что строка пробная, а не гадать,
// почему в дебиторке висит долг на 1 ₽.
//
// Почему это важнее суммы. Денег в пробных записях почти нет —
// 523 157 ₽ из 412 253 640 ₽, 0,13 % долга (на 28.07.2026). Но экран открывается сортировкой
// по сроку оплаты, а у пробных записей срок самый старый — и в первой десятке строк их
// оказывается пять из десяти. Портится не итог, портится первый экран.
//
// Признаки выведены из данных зеркала, а не придуманы: регулярное выражение проверено на всех
// 16 253 записях смарт-процесса 134 (на 28.07.2026) — ложных срабатываний нет (боевые
// «Тестирование ССОО», «Проверка РНО», «тестер эхолота» под него не попадают). Список
// компаний — те карточки Bitrix, у которых название само объявляет их заглушкой.
//
// ВАЖНО: пометка ничего не вычитает. Итоги по умолчанию считаются со всеми строками, как
// считались; «скрыть пробные» — отдельный выключатель, по умолчанию выключенный.

/**
 * Компании-заглушки Bitrix: контрагенты, заведённые ради проверки, а не ради работы.
 * id зашиты намеренно, как `PAID_CATEGORY_ID`: вывести их из данных нельзя — в списке
 * платежей приходит только `companyId`, названия компаний экран догружает отдельно и не
 * на всех строках. Заведут новую заглушку — правка здесь, в одном месте.
 *
 * Проверено по `companies` на 27.07.2026: «test», «testcomp», «Рога и Копыта», «Вася пуп»,
 * «Тестовая компания», «Алина тестовая компания», «тест», «Тестовая Компания 2», «Тестовая»,
 * «ООО Битриксмены», «ООО Битриксмены 2.0», «тестовая», «ZZ_TEST_REQ удалить»,
 * «ZZ ТЕСТ вебхук core (можно удалить)».
 *
 * Сюда НЕ входят настоящие клиенты, у которых в названии есть слово «тест»
 * («ООО «ЭКБ ТЕСТ»», «2TEST», «ПРОММАШ ТЕСТ МЕТРОЛОГИЯ», «ТестКомплект» и ещё шесть):
 * они платят деньги, и прятать их долг было бы хуже любой пробной записи.
 */
export const PILOT_COMPANIES: readonly string[] = [
  '1662', '1692', '6492', '15071', '16407', '19670', '19999',
  '20251', '20290', '21043', '21184', '21775', '21781', '21872',
]

/**
 * Названия, которые сами объявляют запись пробной.
 *
 * Каждая альтернатива узкая по одной причине: в морском сервисе «тест» и «проверка» —
 * обычные рабочие слова. «Тестирование ССОО», «Проверка РНО», «проведение теста SSAS»,
 * «продажа тестера эхолота» — это работа, за которую выставлен счёт, и подпись «пробная»
 * на таком платеже была бы ложью. Поэтому ловим только обороты, у которых рабочего смысла
 * нет: «ТЕСТТЕСТ», «Вася Пупкин» (местный аналог John Doe), «сделка в отладке», «тестовая
 * сделка/компания/заявка», «для тестирования (рассылки)» и хвост названия, состоящий
 * ровно из слова «тест»/«тестовая».
 */
export const PILOT_TITLE_RE =
  /тесттест|пупкин|в отладке|тестов(ая|ое|ый|ую)\s+(сделк|сделочк|компан|заявк)|для тестировани|тестирование отгрузк|(^|[/|])\s*тест(ов(ая|ое|ый))?\s*$/i

/**
 * Пробная ли запись — и по какому признаку. Возвращаем причину строкой, а не `true`:
 * подпись «пробная» без объяснения — это просьба поверить на слово, а человеку нужно
 * решить, согласен ли он. Пусто — запись обычная.
 */
export function pilotReason(title: string, companyId: string): string {
  const reasons: string[] = []
  if (companyId && PILOT_COMPANIES.includes(companyId)) reasons.push('контрагент-заглушка')
  if (PILOT_TITLE_RE.test(title)) reasons.push('название помечено как пробное')
  return reasons.join('; ')
}

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v))

// ─────────── корзины срочности ───────────
// В Bitrix стадии воронки 27/28 — это и есть корзины срока («На сегодня», «На этой неделе»,
// «Больше недели», «Без срока», «Просрочены»), и двигает их бизнес-процесс. Мы считаем корзину
// САМИ, по «Примерной дате оплаты»: дата — это правда, а стадия может отставать от неё
// (расхождение №6 в карте полей). Границы у нас в скользящих днях, у БП — возможно, по
// календарным неделям, поэтому корзина и стадия у отдельной строки могут не совпасть;
// стадию показываем рядом, чтобы это было видно, а не спрятано.

export type BucketKey = 'overdue' | 'today' | 'week' | 'later' | 'nodate'

export const BUCKETS: Array<{ key: BucketKey; label: string; hint: string; hot?: boolean }> = [
  { key: 'overdue', label: 'Просрочено', hint: 'срок оплаты в прошлом', hot: true },
  { key: 'today', label: 'На сегодня', hint: 'срок оплаты — сегодня' },
  { key: 'week', label: 'До 7 дней', hint: 'срок в пределах недели' },
  { key: 'later', label: 'Больше недели', hint: 'срок дальше чем через 7 дней' },
  { key: 'nodate', label: 'Без срока', hint: 'примерная дата оплаты не заполнена' },
]

/** Разобранный платёж: всё, что экрану нужно считать, посчитано один раз здесь. */
export interface PaymentView {
  id: string
  title: string
  stageId: string
  stageKind: StageKind
  categoryId: string
  companyId: string
  /** Сделка: сначала поле-привязка, потом системный родитель (на части записей пусто одно из них). */
  dealId: string
  assignedById: string
  /**
   * «Ответственный за дебиторку». Пусто — привязки в Bitrix нет, и экран обязан взять
   * ответственного самой карточки. Ноль сюда не попадает: Bitrix обозначает нулём именно
   * пустую привязку, а непустая строка '0' тихо отменяла бы подстановку (было «#0»
   * вместо фамилии — платёж 17254 сделки 107623).
   */
  respDebtId: string
  invoice: string
  dept: string
  /** «Сумма платежа». null — не заполнено ни money-поле, ни стандартная сумма. */
  value: Money | null
  /**
   * Сумма взята из стандартного `opportunity`, потому что money-поле пусто. Это подпорка:
   * у платежей сумма живёт в money-поле, а `opportunity` часто 0 — но если money-поля нет,
   * а стандартная сумма есть, показать её честнее, чем показать ноль. В интерфейсе помечаем.
   */
  valueFallback: boolean
  /** «Сумма для долгов» — справочно, рядом с нашим расчётом (см. ниже). */
  debtSum: number | null
  received: number | null
  /** Наш остаток долга: сумма платежа минус поступление. */
  remainder: number
  currency: string
  dueDay: string | null
  paidDay: string | null
  /** Оплачен по любому из признаков Bitrix. */
  paid: boolean
  /** Стадия «провал» — списанный/безнадёжный платёж. */
  failed: boolean
  /** Дней просрочки: >0 — просрочен, ≤0 — срок ещё не вышел. null — срока нет. */
  overdueDays: number | null
  bucket: BucketKey
  /** Признаки оплаты противоречат друг другу — «грязный» платёж. Пусто, если всё сходится. */
  dirty: string
  /**
   * Запись похожа на пробную (обкатка БП, обучение, отладка) — и по какому признаку.
   * Пусто у обычных платежей. Ни на одну цифру не влияет: это подпись, а не фильтр.
   */
  pilot: string
}

/**
 * Остаток долга. Берём «Сумма платежа − Сумма поступления».
 *
 * Почему так, а не как на порталах. 55 при разности ≤0 подставляет ВСЮ сумму платежа
 * (`worker.js:3937`) — то есть полностью оплаченный платёж считается долгом целиком;
 * exec берёт «Сумму для долгов» и поступления не вычитает вовсе. Обе цифры неверны
 * по-своему, поэтому считаем прямо, а «Сумму для долгов» показываем рядом справочной
 * колонкой — до выяснения в Bitrix, кто её пишет (карта полей §9).
 */
function remainderOf(value: Money | null, received: number | null): number {
  const gross = value?.amount ?? 0
  return Math.max(gross - (received ?? 0), 0)
}

/**
 * Оплачен ли платёж. Используем ВСЕ признаки сразу (карта полей §7, расхождение №2):
 * дата «Оплачен», флаг «Оплачен», воронка «Оплачено», стадия с семантикой «успех».
 * Каждого по отдельности достаточно, чтобы не звонить клиенту по уже закрытому долгу.
 */
function paidFlags(p: Payment, stageKind: StageKind, categoryId: string) {
  const paidDay = isoDay(p[PAY.paidDate])
  const flag = str(p[PAY.paidFlag]).trim().toUpperCase()
  const byFlag = flag === 'Y' || flag === 'TRUE' || flag === '1'
  const byCategory = categoryId === PAID_CATEGORY_ID
  const byStage = stageKind === 'won'
  return { paidDay, byDate: !!paidDay, byFlag, byCategory, byStage,
           paid: !!paidDay || byFlag || byCategory || byStage }
}

/** Разобрать запись 134. `refs` нужен для семантики стадии; без него судим по коду. */
export function paymentView(p: Payment, refs?: Pick<RefsApi, 'itemStageKind' | 'hasItemStages'>,
                            today = todayIso()): PaymentView {
  const stageId = str(p.stageId)
  const stageKind: StageKind = refs ? refs.itemStageKind(stageId) : 'work'
  // Знаем ли мы смысл стадии по-настоящему. Без справочника любая пользовательская стадия
  // выглядит как «в работе», и на этом нельзя строить вывод «данные противоречивы».
  const stageKnown = refs?.hasItemStages(PAY_TYPE_ID) ?? false
  const categoryId = str(p.categoryId)
  // Сумма: money-поле платежа, и только если оно пусто — стандартное opportunity (ненулевое).
  const money = parseMoney(p[PAY.value])
  const std = toNumber(p.opportunity)
  const valueFallback = !money && std !== null && std !== 0
  const value: Money | null = money
    ?? (valueFallback ? { amount: std as number, currency: str(p.currencyId) || 'RUB' } : null)
  const received = toNumber(p[PAY.received])
  const flags = paidFlags(p, stageKind, categoryId)

  const dueDay = isoDay(p[PAY.dueDate])
  const overdueDays = dueDay ? daysPast(dueDay, today) : null

  let bucket: BucketKey = 'nodate'
  if (overdueDays !== null) {
    if (overdueDays > 0) bucket = 'overdue'
    else if (overdueDays === 0) bucket = 'today'
    else if (overdueDays >= -7) bucket = 'week'
    else bucket = 'later'
  }

  // «Грязный» платёж: один признак говорит «оплачен», другой — «нет». Это не наша ошибка
  // расчёта, а рассинхрон в Bitrix, и именно из-за него 55 и exec показывают разные суммы.
  const dirtyParts: string[] = []
  if (!flags.byDate && (flags.byFlag || flags.byCategory || flags.byStage)) {
    dirtyParts.push('помечен оплаченным, но дата оплаты не заполнена')
  }
  if (stageKnown && flags.byDate && stageKind === 'work' && categoryId !== PAID_CATEGORY_ID) {
    dirtyParts.push('дата оплаты есть, но стадия и воронка не «оплачено»')
  }

  const title = str(p.title)
  // Компанию берём той же формулой, что и ниже: у пробной записи привязки может не быть
  // вовсе (платёж 6698 «Сделка в отладке» — ни компании, ни сделки), и тогда решает название.
  const companyId = refId(p.companyId) || refId(p[PAY.companyField])

  return {
    id: str(p.id),
    title,
    stageId,
    stageKind,
    categoryId,
    // Все привязки — через refId: у Bitrix «не привязано» это ноль, а не пустота, и без
    // чистки ноль ведёт себя как заполненное значение — отменяет запасной вариант справа
    // и печатается на экране ссылкой «#0».
    companyId,
    dealId: refId(p[PAY.deal]) || refId(p.parentId2),
    assignedById: refId(p.assignedById),
    respDebtId: refId(p[PAY.respDebt]),
    invoice: Array.isArray(p[PAY.invoice])
      ? (p[PAY.invoice] as unknown[]).map(str).filter(Boolean).join(', ')
      : str(p[PAY.invoice]),
    dept: str(p[PAY.dept]),
    value,
    valueFallback,
    debtSum: toNumber(p[PAY.debtSum]),
    received,
    remainder: remainderOf(value, received),
    currency: value?.currency || str(p.currencyId) || 'RUB',
    dueDay,
    paidDay: flags.paidDay,
    paid: flags.paid,
    failed: stageKind === 'lose',
    overdueDays,
    bucket,
    dirty: dirtyParts.join('; '),
    pilot: pilotReason(title, companyId),
  }
}

export interface BucketTotal {
  count: number
  sum: number
}

/**
 * Итоги по корзинам срочности. Считаются по ПЕРЕДАННЫМ строкам — то есть по загруженной
 * странице, а не по всей дебиторке: агрегата у ядра нет, а тянуть 16 тысяч записей на клиент
 * ради суммы — это 330 запросов (см. `ДЕБИТОРКА_карта_полей.md` §8). Экран это подписывает.
 */
export function summarize(rows: PaymentView[]): {
  buckets: Record<BucketKey, BucketTotal>
  total: BucketTotal
  paid: BucketTotal
  failed: BucketTotal
  dirty: number
  /**
   * Сколько из «Итого долг» приходится на пробные записи. Считается ВНУТРИ итога, а не
   * вместо него: цифры экрана остаются прежними, а рядом с ними появляется честная оговорка
   * «из них N пробных на M ₽». Вычитать молча нельзя — это была бы третья версия правды
   * о долге, ровно то, от чего уходим.
   */
  pilot: BucketTotal
} {
  const empty = (): BucketTotal => ({ count: 0, sum: 0 })
  const buckets = {
    overdue: empty(), today: empty(), week: empty(), later: empty(), nodate: empty(),
  } as Record<BucketKey, BucketTotal>
  const total = empty()
  const paid = empty()
  const failed = empty()
  const pilot = empty()
  let dirty = 0

  for (const r of rows) {
    if (r.dirty) dirty += 1
    // оплаченные и списанные в корзины срока не попадают: это уже не долг,
    // иначе «просрочено» раздувается закрытыми платежами (расхождение №3)
    if (r.paid) {
      paid.count += 1
      paid.sum += r.value?.amount ?? 0
      continue
    }
    if (r.failed) {
      failed.count += 1
      failed.sum += r.remainder
      continue
    }
    buckets[r.bucket].count += 1
    buckets[r.bucket].sum += r.remainder
    total.count += 1
    total.sum += r.remainder
    // пробная строка учтена в корзине и в итоге выше — здесь только считаем её отдельно
    if (r.pilot) {
      pilot.count += 1
      pilot.sum += r.remainder
    }
  }
  return { buckets, total, paid, failed, dirty, pilot }
}
